import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en.json';
import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';

export type CountryCode =
  | 'AC'
  | 'AD'
  | 'AE'
  | 'AF'
  | 'AG'
  | 'AI'
  | 'AL'
  | 'AM'
  | 'AO'
  | 'AR'
  | 'AS'
  | 'AT'
  | 'AU'
  | 'AW'
  | 'AX'
  | 'AZ'
  | 'BA'
  | 'BB'
  | 'BD'
  | 'BE'
  | 'BF'
  | 'BG'
  | 'BH'
  | 'BI'
  | 'BJ'
  | 'BL'
  | 'BM'
  | 'BN'
  | 'BO'
  | 'BQ'
  | 'BR'
  | 'BS'
  | 'BT'
  | 'BW'
  | 'BY'
  | 'BZ'
  | 'CA'
  | 'CC'
  | 'CD'
  | 'CF'
  | 'CG'
  | 'CH'
  | 'CI'
  | 'CK'
  | 'CL'
  | 'CM'
  | 'CN'
  | 'CO'
  | 'CR'
  | 'CU'
  | 'CV'
  | 'CW'
  | 'CX'
  | 'CY'
  | 'CZ'
  | 'DE'
  | 'DJ'
  | 'DK'
  | 'DM'
  | 'DO'
  | 'DZ'
  | 'EC'
  | 'EE'
  | 'EG'
  | 'EH'
  | 'ER'
  | 'ES'
  | 'ET'
  | 'FI'
  | 'FJ'
  | 'FK'
  | 'FM'
  | 'FO'
  | 'FR'
  | 'GA'
  | 'GB'
  | 'GD'
  | 'GE'
  | 'GF'
  | 'GG'
  | 'GH'
  | 'GI'
  | 'GL'
  | 'GM'
  | 'GN'
  | 'GP'
  | 'GQ'
  | 'GR'
  | 'GT'
  | 'GU'
  | 'GW'
  | 'GY'
  | 'HK'
  | 'HN'
  | 'HR'
  | 'HT'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IL'
  | 'IM'
  | 'IN'
  | 'IO'
  | 'IQ'
  | 'IR'
  | 'IS'
  | 'IT'
  | 'JE'
  | 'JM'
  | 'JO'
  | 'JP'
  | 'KE'
  | 'KG'
  | 'KH'
  | 'KI'
  | 'KM'
  | 'KN'
  | 'KP'
  | 'KR'
  | 'KW'
  | 'KY'
  | 'KZ'
  | 'LA'
  | 'LB'
  | 'LC'
  | 'LI'
  | 'LK'
  | 'LR'
  | 'LS'
  | 'LT'
  | 'LU'
  | 'LV'
  | 'LY'
  | 'MA'
  | 'MC'
  | 'MD'
  | 'ME'
  | 'MF'
  | 'MG'
  | 'MH'
  | 'MK'
  | 'ML'
  | 'MM'
  | 'MN'
  | 'MO'
  | 'MP'
  | 'MQ'
  | 'MR'
  | 'MS'
  | 'MT'
  | 'MU'
  | 'MV'
  | 'MW'
  | 'MX'
  | 'MY'
  | 'MZ'
  | 'NA'
  | 'NC'
  | 'NE'
  | 'NF'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NP'
  | 'NR'
  | 'NU'
  | 'NZ'
  | 'OM'
  | 'PA'
  | 'PE'
  | 'PF'
  | 'PG'
  | 'PH'
  | 'PK'
  | 'PL'
  | 'PM'
  | 'PR'
  | 'PS'
  | 'PT'
  | 'PW'
  | 'PY'
  | 'QA'
  | 'RE'
  | 'RO'
  | 'RS'
  | 'RU'
  | 'RW'
  | 'SA'
  | 'SB'
  | 'SC'
  | 'SD'
  | 'SE'
  | 'SG'
  | 'SH'
  | 'SI'
  | 'SJ'
  | 'SK'
  | 'SL'
  | 'SM'
  | 'SN'
  | 'SO'
  | 'SR'
  | 'SS'
  | 'ST'
  | 'SV'
  | 'SX'
  | 'SY'
  | 'SZ'
  | 'TA'
  | 'TC'
  | 'TD'
  | 'TG'
  | 'TH'
  | 'TJ'
  | 'TK'
  | 'TL'
  | 'TM'
  | 'TN'
  | 'TO'
  | 'TR'
  | 'TT'
  | 'TV'
  | 'TW'
  | 'TZ'
  | 'UA'
  | 'UG'
  | 'US'
  | 'UY'
  | 'UZ'
  | 'VA'
  | 'VC'
  | 'VE'
  | 'VG'
  | 'VI'
  | 'VN'
  | 'VU'
  | 'WF'
  | 'WS'
  | 'XK'
  | 'YE'
  | 'YT'
  | 'ZA'
  | 'ZM'
  | 'ZW';

const countries = getCountries();
const countriesOptions = countries
  .map((item) => ({ country: item, label: en[item], code: '+' + getCountryCallingCode(item) }))
  .sort((i1, i2) => {
    if (i1.label < i2.label) return -1;
    if (i1.label > i2.label) return 1;
    return 0;
  });

const normalizeValue = (v?: string) => {
  if (!v) return v;
  if (v[0] === '+') return v.slice(1);
  return v;
};

interface CountrySelectProps {
  className?: string;
  // labels
  name?: string;
  formStyle?: boolean;
  required?: boolean;
  readOnly?: boolean;
  value?: string;
  error?: ReactNode;
  onChange?: (value: string) => void;
}

export const CountrySelect = (props: CountrySelectProps) => {
  const { t } = useTranslation();

  const val = normalizeValue(props.value);
  const currentValue = countriesOptions.find((country) => country.code === `+${val}`);

  return (
    <div
      className={clsx(
        'transaction-all duration-300 min-w-[94px] mr-3 relative ',
        props.formStyle
          ? '!h-10 tracking-[0.3px] border flex items-center justify-start border-[#aaaab2] shadow-[inset_0_2px_6px_0_rgba(85,85,90,0.39)] px-2 text-lg font-semibold'
          : 'flex items-center justify-start mr-3 py-0 px-2 relative h-[50px] border text-[#4c4c52]  hover:border-black transition-all ease  duration-300',
        !props.formStyle &&
          (!PaletteUtils.isBordierPalette
            ? 'bg-[#303034] !border-[#6d6d75] text-[#dadada] hover:!border-[#9f9fa5] focus:!border-[#9f9fa5]'
            : 'text-[#4c4c52] bg-[#fcfcfc] border-[#e1e1e1] hover:border-[#979797] focus:border-[#979797]'),
        {
          '!border-[#d34014]': !!props.error,
          '!hover:border-[#979797]': !props.error,
        },

        props.className,
      )}
    >
      <select
        className={clsx('cursor-pointer h-full left-0 opacity-0 absolute top-0 w-full text-black')}
        name={`${props.name}_code`}
        required={props.required}
        value={val}
        onChange={(e) => props.onChange?.(getCountryCallingCode(e.target.value as CountryCode))}
      >
        {countriesOptions.map((item) => (
          <option className='hover:bg-[#f1f8fb] ' key={item.country} value={item.country}>
            {item.label} {item.code}
          </option>
        ))}
      </select>
      {currentValue ? (
        <>
          <div className='w-6 h-4 text-xl'>
            <img src={`https://catamphetamine.gitlab.io/country-flag-icons/3x2/${currentValue.country}.svg`} role='presentation' />
          </div>
          <span className='ml-2 text-lg font-semibold'>+{val}</span>
        </>
      ) : (
        <span className='text-xl font-semibold tracking-[0.3px]'>{t('placeholders.select')}</span>
      )}
    </div>
  );
};
